import * as React from 'react';
// Import hooks.
import {
	useInputDropdown,
	UseInputDropdownResults,
	useInputFile,
	UseInputFileResults,
	UseInputFormResult,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
// Import enumerations.
import {
	FileExtension,
} from '@app/enumerations';
// Import api models.
import {
	PolicyNoteDocumentWithData,
	PossibleDocCategory,
} from '@app/models/api';
// Import app models.
import {
	DropdownItem,
	FileData,
} from '@app/models/app';


interface UseFormPopupAddPolicyNoteBaseParameters {
	id: string;
	possibleDocCategories: PossibleDocCategory[];
	policyTermYears: number[];
}

interface UseFormPopupAddPolicyNoteBaseResults {
	noteInput: UseInputTextResults;
	fileInput: UseInputFileResults;
	documentTypeInput: UseInputDropdownResults;
	documentYearInput: UseInputDropdownResults;
	createPolicyNoteDocument: () => PolicyNoteDocumentWithData;
	checkValidationWithFocus: () => boolean;
	flagAllInputsToShowValidations: () => void;
	isDocumentYearRequired: () => boolean;
}

const useFormPopupAddPolicyNoteBase = (params: UseFormPopupAddPolicyNoteBaseParameters): UseFormPopupAddPolicyNoteBaseResults => {

	// #region exported methods

	/**
	 * Creates a document record by collecting all user inputs.
	 **/
	const createPolicyNoteDocument = (): PolicyNoteDocumentWithData => {
		let policyNoteDocument: PolicyNoteDocumentWithData = new PolicyNoteDocumentWithData();
		policyNoteDocument.docName = !String.isNullOrWhiteSpace(fileInput.value.fileName) ? fileInput.value.fileName : null;
		policyNoteDocument.data = fileInput.value.fileInput?.toUint8Array() ?? null;
		policyNoteDocument.docCategory = !String.isNullOrWhiteSpace(documentTypeInput.value) ? documentTypeInput.value : null;
		policyNoteDocument.docYear = !isNaN(parseInt(documentYearInput.value))
			? parseInt(documentYearInput.value)
			: null;
		return policyNoteDocument;
	}

	/**
	 * Method to check validation with focus. 
	 **/
	const checkValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<string>(noteInput)
			&& checkInputValidationWithFocus<FileData>(fileInput)
			&& checkInputValidationWithFocus<string>(documentTypeInput)
			&& checkInputValidationWithFocus<string>(documentYearInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		noteInput.changeShowValidationFlag(true);
		fileInput.changeShowValidationFlag(true);
		documentTypeInput.changeShowValidationFlag(true);
		documentYearInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the document year is required. 
	 **/
	const isDocumentYearRequired = (): boolean => {
		if (String.isNullOrWhiteSpace(documentTypeInput.value)) {
			return false;
		}

		let selectedDocCategory: PossibleDocCategory | undefined = params
			.possibleDocCategories
			.find((possibleDocCategory: PossibleDocCategory) => possibleDocCategory.code === documentTypeInput.value);

		return selectedDocCategory?.isYearRequired ?? false;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the document type is required. 
	 **/
	const isDocumentTypeRequired = (): boolean => {
		return !String.isNullOrWhiteSpace(fileInput.value.fileName)
			&& fileInput.value.fileInput !== undefined;
	}

	/**
	 * Converts the document types to a list of dropdown items. 
	 **/
	const getDocumentTypeDropdownItems = (): DropdownItem[] => {
		return params.possibleDocCategories.map((possibleDocCategory: PossibleDocCategory) => {
			return new DropdownItem({
				label: possibleDocCategory.code !== null ? possibleDocCategory.code : String.empty(),
				value: possibleDocCategory.code !== null ? possibleDocCategory.code : String.empty(),
			});
		})
	}

	/**
	 * Converts the document years to a list of dropdown items. 
	 **/
	const getDocumentYearDropdownItems = (): DropdownItem[] => {
		return params.policyTermYears.map((policyTermYear: number) => {
			return new DropdownItem({
				label: policyTermYear.toString(),
				value: policyTermYear.toString(),
			});
		})
	}

	/**
	 * Fetches the default document type as dropdown item. 
	 **/
	const getDefaultDocumentTypeDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Fetches the default document year as dropdown item. 
	 **/
	const getDefaultDocumentYearDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for note. 
	 **/
	const generateIdForNote = (): string => {
		return `${generateIdForForm()}-note`;
	}

	/**
	 * Generate id for file. 
	 **/
	const generateIdForFile = (): string => {
		return `${generateIdForForm()}-file`;
	}

	/**
	 * Generate id for document type. 
	 **/
	const generateIdForDocumentType = (): string => {
		return `${generateIdForForm()}-documentType`;
	}

	/**
	 * Generate id for document year. 
	 **/
	const generateIdForDocumentYear = (): string => {
		return `${generateIdForForm()}-documentYear`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	let allowedFileExtensions: FileExtension[] = [
		FileExtension.ZIP,
		FileExtension.PDF,
		FileExtension.TXT,
		FileExtension.JPG,
		FileExtension.PNG,
	];

	// Instantiate the 'note' input hook.
	const noteInput: UseInputTextResults = useInputText({
		id: generateIdForNote(),
		label: 'Note',
		required: true,
		disabled: false,
		value: String.empty(),
		showValidation: false,
	});

	// Instantiate the 'file' input hook.
	const fileInput: UseInputFileResults = useInputFile({
		id: generateIdForFile(),
		label: `(${allowedFileExtensions.join(', ')})`,
		required: false,
		disabled: false,
		file: new FileData({
			fileName: String.empty(),
			fileInput: undefined,
		}),
		allowedFileExtensions: allowedFileExtensions,
		showValidation: false,
	});

	// Instantiate the 'document type' input hook.
	const documentTypeInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForDocumentType(),
		label: 'Document Type',
		required: isDocumentTypeRequired(),
		disabled: !isDocumentTypeRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultDocumentTypeDropdownItem(),
		dropdownItems: getDocumentTypeDropdownItems(),
		showValidation: false,
	});

	// Instantiate the 'document year' input hook.
	const documentYearInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForDocumentYear(),
		label: 'Term Year',
		required: isDocumentYearRequired(),
		disabled: !isDocumentYearRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultDocumentYearDropdownItem(),
		dropdownItems: getDocumentYearDropdownItems(),
		showValidation: false,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, or when the file input is updated.
	React.useEffect(() => {
		// Reset the doc type and year if the doc type is no longer required.
		if (!isDocumentTypeRequired()) {
			documentTypeInput.setInput(String.empty());
			documentYearInput.setInput(String.empty());
		}
	}, [fileInput.value]);

	// Called when the hook is initially mounted, or when the document type input is updated.
	React.useEffect(() => {
		// Reset the doc year if it's not required.
		if (!isDocumentYearRequired()) {
			documentYearInput.setInput(String.empty());
		}
	}, [documentTypeInput.value]);

	// #endregion life-cycle methods.


	return {
		noteInput: noteInput,
		fileInput: fileInput,
		documentTypeInput: documentTypeInput,
		documentYearInput: documentYearInput,
		createPolicyNoteDocument: createPolicyNoteDocument,
		checkValidationWithFocus: checkValidationWithFocus,
		flagAllInputsToShowValidations: flagAllInputsToShowValidations,
		isDocumentYearRequired: isDocumentYearRequired,
	};
}

// Export parameters.
export {
	UseFormPopupAddPolicyNoteBaseParameters,
}

// Export results.
export {
	UseFormPopupAddPolicyNoteBaseResults,
}

export default useFormPopupAddPolicyNoteBase;